import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { FACT_SPECS, READONLY_JSON_SPECS, draftChanges, describeProblem, fieldLabel, inputText, showValue, type FactsView, type Problem } from "./factsModel";
import { busyButton, card, labelled, notice, problemNotice, select, textArea, textInput } from "./kit";
import { enc, type Workspace } from "./workspace";

// APPROVED FACTS: what CloudPush uses for content and sites. Read view, and an edit form that sends only the changed fields with the
// revision it loaded (`ifRevision`). If something else saved first, the server answers 409 STALE_REVISION and the card says so in words,
// keeps what the person typed, and offers to reload: it never overwrites silently and never pretends the save worked.

export function approvedCard(ws: Workspace): HTMLElement {
  const body = h("div", { class: "fx-card__body" });
  const out = card("fx-approved", "Approved facts", h("p", { class: "muted" }, "These values are used for content and websites. Information that is not confirmed belongs under Unresolved information, not here."), body);

  let editing = false;
  /** What the person typed, by field. Only fields they touched are present. */
  let drafts: Record<string, string> = {};
  let reason = "";
  let result: HTMLElement | null = null;
  let conflict: { problem: Problem; theirs: { field: string; mine: string; now: string }[] } | null = null;
  let errors: Record<string, string> = {};
  /** The approved values the person started editing from (to tell what changed under them). */
  let baseline: Record<string, unknown> = {};
  const form = h("form", { class: "fx-form", novalidate: true, "aria-label": "Edit approved facts" });

  const facts = (): FactsView | null => ws.facts;

  function readView(f: FactsView): HTMLElement {
    const rows = [...FACT_SPECS, ...READONLY_JSON_SPECS].flatMap((s) => [h("dt", null, s.label), h("dd", { class: "fx-value", dataset: { fact: s.key } }, showValue(f.approved[s.key], 600))]);
    return h("dl", { class: "deflist fx-deflist" }, ...rows);
  }

  function header(f: FactsView): HTMLElement {
    return h(
      "div",
      { class: "fx-bar" },
      pill(`Revision ${f.revision}`, "neutral"),
      h("span", { class: "muted" }, "Each saved change adds one revision and one history entry."),
      editing ? null : h("button", { type: "button", id: "fx-edit", on: { click: startEdit } }, "Edit facts"),
    );
  }

  function startEdit() {
    const f = facts();
    if (!f) return;
    editing = true;
    drafts = {};
    reason = "";
    result = null;
    conflict = null;
    errors = {};
    baseline = structuredClone(f.approved);
    buildForm();
    paint();
    form.querySelector<HTMLElement>("input,textarea,select")?.focus();
  }
  function cancel() {
    editing = false;
    conflict = null;
    errors = {};
    paint();
  }

  /** The form is built once per editing session so typing is never interrupted; `syncMarks` updates what changes around the inputs. */
  function buildForm() {
    const f = facts()!;
    const fields = FACT_SPECS.map((spec) => {
      const initial = inputText(spec, f.approved[spec.key]);
      const onInput = (v: string) => {
        drafts[spec.key] = v;
        delete errors[spec.key];
        syncMarks();
      };
      const input: HTMLElement =
        spec.kind === "textarea" ? textArea(initial, { name: spec.key, rows: 3, onInput }) : spec.kind === "json" ? textArea(initial, { name: spec.key, rows: 5, mono: true, onInput }) : spec.kind === "select" ? select(spec.options ?? [], initial, { name: spec.key, onChange: onInput }) : textInput(initial, { name: spec.key, type: spec.kind === "number" ? "text" : "text", onInput });
      input.dataset.fact = spec.key;
      return labelled(spec.label, input, { help: spec.help, wide: spec.kind === "json" || spec.kind === "textarea" });
    });
    const reasonInput = textInput("", { name: "reason", onInput: (v) => void (reason = v) });
    const save = busyButton({ label: "Save changes", busyLabel: "Saving…", id: "fx-save", type: "submit", onClick: () => save_() });
    form.replaceChildren(
      h("div", { class: "fx-grid" }, ...fields),
      labelled("Reason (optional, recorded in the history)", reasonInput, { wide: true }),
      h("div", { class: "fx-conflict-slot", id: "fx-conflict-slot", "aria-live": "polite" }),
      h("div", { class: "btn-group" }, save, h("button", { type: "button", class: "secondary", id: "fx-cancel", on: { click: cancel } }, "Cancel")),
      h("div", { id: "fx-form-result", "aria-live": "polite" }),
    );
    form.onsubmit = (e) => {
      e.preventDefault();
      void save.click();
    };
  }

  /** Which fields currently differ from the approved value, shown beside the inputs so nothing is a surprise at save time. */
  function syncMarks() {
    const f = facts();
    if (!f) return;
    const { changes } = draftChanges(drafts, f.approved);
    for (const spec of FACT_SPECS) {
      const field = form.querySelector<HTMLElement>(`[data-fact="${spec.key}"]`)?.closest(".fx-field");
      field?.classList.toggle("fx-field--changed", spec.key in changes);
      const err = field?.querySelector(".fx-error");
      if (errors[spec.key]) {
        if (!err) field?.append(h("div", { class: "fx-error", role: "alert" }, errors[spec.key]));
      } else err?.remove();
    }
    const slot = form.querySelector("#fx-conflict-slot");
    if (slot) {
      const n = Object.keys(changes).length;
      slot.replaceChildren(...(conflict ? [conflictBlock()] : []), h("p", { class: "muted", id: "fx-changed-count" }, n === 0 ? "No changes yet." : `${n} ${n === 1 ? "field" : "fields"} changed: ${Object.keys(changes).map(fieldLabel).join(", ")}.`));
    }
  }

  function conflictBlock(): HTMLElement {
    const c = conflict!;
    return h(
      "div",
      { class: "fx-conflict", role: "alert", id: "fx-conflict" },
      h("strong", null, "Conflict, reload"),
      h("p", null, c.problem.message),
      c.theirs.length
        ? h("div", null, h("p", null, "These fields changed on the server while you were editing:"), h("ul", null, ...c.theirs.map((t) => h("li", null, `${fieldLabel(t.field)}: now ${t.now}; your edit ${t.mine}.`))))
        : h("p", { class: "muted" }, "None of the fields you edited changed; something else was saved first."),
      h(
        "div",
        { class: "btn-group" },
        busyButton({ label: "Reload and keep my edits", busyLabel: "Reloading…", id: "fx-reload-keep", onClick: () => reload(true) }),
        busyButton({ label: "Discard my edits and reload", busyLabel: "Reloading…", variant: "secondary", id: "fx-reload-discard", onClick: () => reload(false) }),
      ),
    );
  }

  async function reload(keep: boolean) {
    const before = baseline;
    const f = await ws.refreshFacts();
    if (!f || !ws.life.alive()) return;
    if (!keep) return cancel();
    // keep the typed values, but now they are measured against what the server has
    baseline = structuredClone(f.approved);
    const theirs = Object.keys(drafts)
      .filter((k) => JSON.stringify(before[k] ?? null) !== JSON.stringify(f.approved[k] ?? null))
      .map((k) => ({ field: k, mine: showValue(drafts[k], 120), now: showValue(f.approved[k], 120) }));
    conflict = null;
    result = notice("info", `Reloaded: you now have revision ${f.revision}. Your edits are still in the form${theirs.length ? `; ${theirs.length === 1 ? "one field was" : "some fields were"} also changed by someone else (${theirs.map((t) => fieldLabel(t.field)).join(", ")}): check ${theirs.length === 1 ? "it" : "them"} before saving` : ""}.`);
    paint();
    syncMarks();
  }

  async function save_() {
    const f = facts();
    if (!f) return;
    const { changes, errors: errs } = draftChanges(drafts, f.approved);
    errors = errs;
    result = null;
    if (Object.keys(errs).length) {
      syncMarks();
      showResult(notice("bad", "Fix the highlighted fields first. Nothing was sent."));
      form.querySelector<HTMLElement>(`[data-fact="${Object.keys(errs)[0]}"]`)?.focus();
      return;
    }
    if (Object.keys(changes).length === 0) return void showResult(notice("info", "Nothing changed, so nothing was saved."));
    const ifRevision = f.revision;
    try {
      const res = await api<{ updated?: boolean; diff?: Record<string, unknown>; revision?: number }>(`/v1/clients/${enc(ws.clientId)}/facts`, { method: "PATCH", body: JSON.stringify({ changes, ifRevision, reason: reason.trim() || undefined }) });
      if (!ws.life.alive()) return;
      const saved = Object.keys(res.diff ?? changes);
      editing = false;
      conflict = null;
      result = notice("ok", `Saved${typeof res.revision === "number" ? `: revision ${res.revision}` : ""}. Changed: ${saved.map(fieldLabel).join(", ") || "nothing"}.`);
      paint();
      await ws.wrote();
    } catch (e) {
      if (!ws.life.alive()) return;
      const p = describeProblem(e, "Facts were not saved");
      if (p.kind === "stale") {
        conflict = { problem: p, theirs: [] };
        errors = {};
        syncMarks();
        form.querySelector<HTMLElement>("#fx-conflict")?.scrollIntoView?.({ block: "nearest" });
        form.querySelector<HTMLElement>("#fx-reload-keep")?.focus();
        return;
      }
      if (p.kind === "validation") {
        // mark the fields the server named
        for (const i of p.issues) if (FACT_SPECS.some((s) => s.key === i.field)) errors[i.field] = i.message;
        syncMarks();
      }
      showResult(problemNotice(p));
    }
  }

  function showResult(el: HTMLElement) {
    form.querySelector("#fx-form-result")?.replaceChildren(el);
  }

  function paint() {
    const f = facts();
    if (!f) {
      body.replaceChildren(ws.factsError ? h("div", { class: "error-banner", role: "alert" }, ws.factsError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void ws.refreshFacts() } }, "Try again")) : h("p", { class: "muted" }, "Loading…"));
      return;
    }
    body.replaceChildren(
      header(f),
      ...(f.parseIssues.length ? [notice("warn", "Some stored details could not be read", f.parseIssues)] : []),
      ...(result && !editing ? [result] : []),
      editing ? form : readView(f),
    );
    if (editing) {
      if (result) form.querySelector("#fx-form-result")?.replaceChildren(result);
      syncMarks();
    }
  }

  ws.onFacts(() => {
    // while editing the form is left alone (a refresh must not wipe what is being typed); the header revision is repainted
    if (!editing) paint();
    else body.querySelector(".fx-bar .pill")!.textContent = `Revision ${ws.facts?.revision ?? "?"}`;
  });
  paint();
  return out;
}
